<template>
  <nav class="w-20 bg-primary-700 text-white flex flex-col items-center py-6 space-y-6">
    <!-- Logo -->
    <div class="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
      <span class="text-primary-700 font-bold text-xl">M</span>
    </div>

    <!-- Navigation Items -->
    <div class="flex-1 flex flex-col space-y-4">
      <RouterLink
        v-for="item in navItems"
        :key="item.name"
        :to="item.path"
        v-slot="{ isActive }"
        class="nav-item"
      >
        <button
          :class="[
            'w-12 h-12 rounded-lg flex items-center justify-center transition-colors relative group',
            isActive
              ? 'bg-primary-500 text-white'
              : 'text-primary-100 hover:bg-primary-600'
          ]"
          :title="item.label"
        >
          <component :is="item.icon" class="w-6 h-6" />
          
          <!-- Badge for unread count -->
          <span
            v-if="item.badge && item.badge > 0"
            class="absolute -top-1 -right-1 bg-danger text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center"
          >
            {{ item.badge > 99 ? '99+' : item.badge }}
          </span>
          
          <!-- Tooltip -->
          <span class="absolute left-full ml-3 px-3 py-2 bg-gray-900 text-white text-sm rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            {{ item.label }}
          </span>
        </button>
      </RouterLink>
    </div>

    <!-- User Menu -->
    <div class="relative" ref="userMenuRef">
      <button 
        @click="toggleUserMenu"
        class="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center text-sm font-bold hover:bg-primary-500 transition-colors relative overflow-hidden"
        :class="{ 'ring-2 ring-white': isUserMenuOpen }"
      >
        <!-- Avatar Image -->
        <img 
          v-if="userStore.currentUser?.avatar" 
          :src="userStore.currentUser.avatar" 
          alt="Avatar"
          class="w-full h-full object-cover"
        />
        <!-- Fallback Initials -->
        <span v-else>{{ userInitials }}</span>
        
        <!-- Status indicator -->
        <span 
          v-if="userStore.currentUser?.is_available"
          class="absolute bottom-0 right-0 w-3 h-3 bg-green-400 border-2 border-primary-700 rounded-full z-10"
        ></span>
      </button>
      
      <!-- Dropdown Menu -->
      <Transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <div 
          v-if="isUserMenuOpen"
          class="absolute bottom-0 left-full ml-3 w-64 bg-white rounded-lg shadow-xl border border-gray-200 overflow-hidden z-50"
        >
          <!-- User Info Header -->
          <div class="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-4">
            <div class="flex items-center space-x-3">
              <div class="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-lg font-bold overflow-hidden relative">
                <!-- Avatar Image in Dropdown -->
                <img 
                  v-if="userStore.currentUser?.avatar" 
                  :src="userStore.currentUser.avatar" 
                  alt="Avatar"
                  class="w-full h-full object-cover"
                />
                <span v-else>{{ userInitials }}</span>
              </div>
              <div class="flex-1 min-w-0">
                <p class="font-semibold truncate">{{ userStore.currentUser?.name }}</p>
                <p class="text-xs text-primary-100 truncate">{{ userStore.currentUser?.email }}</p>
              </div>
            </div>
            <!-- Role Badge -->
            <div class="mt-2 flex items-center justify-between">
              <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white">
                {{ roleLabel }}
              </span>
              <!-- Status Toggle -->
              <button 
                @click="toggleAvailability"
                class="flex items-center space-x-1 text-xs bg-white/20 hover:bg-white/30 rounded-full px-2 py-1 transition-colors"
              >
                <span class="w-2 h-2 rounded-full" :class="userStore.currentUser?.is_available ? 'bg-green-400' : 'bg-gray-400'"></span>
                <span>{{ userStore.currentUser?.is_available ? 'Disponível' : 'Ausente' }}</span>
              </button>
            </div>
          </div>

          <!-- Menu Items -->
          <div class="py-2">
            <RouterLink
              to="/profile"
              @click="closeUserMenu"
              class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <UserCircleIcon class="w-5 h-5 mr-3 text-gray-400" />
              Meu Perfil
            </RouterLink>
            
            <RouterLink
              to="/settings"
              @click="closeUserMenu"
              class="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Cog6ToothIcon class="w-5 h-5 mr-3 text-gray-400" />
              Configurações
            </RouterLink>
            
            <div class="border-t border-gray-100 my-2"></div>
            
            <button
              @click="handleLogout"
              class="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
            >
              <ArrowRightOnRectangleIcon class="w-5 h-5 mr-3" />
              Sair
            </button>
          </div>
        </div>
      </Transition>
    </div>
  </nav>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import {
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  FunnelIcon,
  UserGroupIcon,
  DocumentChartBarIcon,
  UsersIcon,
  UserCircleIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  WrenchScrewdriverIcon
} from '@heroicons/vue/24/outline'
import { useUserStore } from '@/stores/user'
import { useConversationsStore } from '@/stores/conversations'

const router = useRouter()
const userStore = useUserStore()
const conversationsStore = useConversationsStore()

// User menu state
const isUserMenuOpen = ref(false)
const userMenuRef = ref(null)

const navItems = computed(() => {
  const items = [
    {
      name: 'inbox',
      path: '/inbox',
      label: 'Conversas',
      icon: ChatBubbleLeftRightIcon,
      badge: conversationsStore.unreadCount
    },
    {
      name: 'pipeline',
      path: '/pipeline',
      label: 'Pipeline',
      icon: FunnelIcon
    },
    {
      name: 'dashboard',
      path: '/dashboard',
      label: 'Dashboard',
      icon: ChartBarIcon
    },
    {
      name: 'leads',
      path: '/leads',
      label: 'Leads',
      icon: UserGroupIcon
    },
    {
      name: 'reports',
      path: '/reports',
      label: 'Relatórios',
      icon: DocumentChartBarIcon
    }
  ]

  // Adicionar "Gestão de Usuários" apenas para admins
  if (userStore.currentUser?.role === 'admin') {
    items.push({
      name: 'users',
      path: '/users',
      label: 'Usuários',
      icon: UsersIcon
    })
    
    // Ferramentas Admin
    items.push({
      name: 'admin-tools',
      path: '/admin/tools',
      label: 'Ferramentas',
      icon: WrenchScrewdriverIcon
    })
  }

  return items
})

const userInitials = computed(() => {
  const name = userStore.currentUser?.name || 'U'
  return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
})

const roleLabel = computed(() => {
  const roleMap = {
    admin: 'Administrador',
    atendente: 'Atendente',
    visualizador: 'Visualizador'
  }
  return roleMap[userStore.currentUser?.role] || 'Usuário'
})

// User menu functions
function toggleUserMenu() {
  isUserMenuOpen.value = !isUserMenuOpen.value
}

function closeUserMenu() {
  isUserMenuOpen.value = false
}

async function handleLogout() {
  try {
    await userStore.logout()
    router.push('/login')
  } catch (error) {
    console.error('Erro ao fazer logout:', error)
  }
}

async function toggleAvailability() {
  try {
    const newStatus = !userStore.currentUser?.is_available
    await userStore.updateAvailability(newStatus)
  } catch (error) {
    console.error('Erro ao alterar disponibilidade:', error)
    alert('❌ Erro ao alterar status de disponibilidade')
  }
}

// Close menu when clicking outside
function handleClickOutside(event) {
  if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    closeUserMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
